// src/pages/CartPage.jsx
import { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  removeFromCart,
  updateQuantity,
  setItemSelected,
  clearCart,
} from "store/slices/cartSlice";

import {
  formatCurrency,
  getCurrentPrice,
  getPromoHint,
  FREE_SHIPPING_THRESHOLD,
  nextFromToday,
  formatLocalDateToronto,
} from "config/config";
import useCartDetails from "hooks/useCartDetails";

import { delayCall } from "../api/util";
import { Breadcrumb, FlipAmount, Loading, ProductImage } from "../common";
import ToastNotification from "../common/toast/Toast";
import { Modal } from "../components";
import { unitLabel } from "../components/product/productPresentation";

import { Button, CartLineItem, Checkbox, Icon, Select } from "@/design-system";
import { selectCartSubtotalWithDiscounts } from "@/redux/slices";

/**
 * The cart page — the reference's CART (full page) screen (grep `===== CART` in
 * `Dens N Dente Tailwind.dc.html`): a `container-lg` two-column grid, lines in one
 * card on the left (full-size `CartLineItem`s, `math` + `unit` on), a sticky
 * "Order summary" card on the right, and the big empty-state card with
 * "Shop the catalogue" / "Order again".
 *
 * App features the reference has no slot for, kept and worked into the anatomy:
 *
 * - **Per-line selection** (checkout takes only selected lines) — a DS `Checkbox`
 *   in each row's left gutter; the summary sums SELECTED lines only, exactly the
 *   old page's semantics (`navigate("/checkout", { state: { selectedIds } })`).
 * - **Subscription state is read-only here** (radios removed on request — the
 *   PDP's purchase options own the choice): a subscribed line carries the blue
 *   "On auto-delivery · next <date>" hint pill, keeping the header's filter and
 *   checkout's recurring-order behaviour legible. Promo wins the pill slot when a
 *   line is both discounted and subscribed (the component's contract). A
 *   Buy-X-Get-Y item below its promotion's minimum shows "Add N to cart to get
 *   promotion price" in the hint pill instead (`getPromoHint`).
 * - **No tax/shipping estimator** — removed on request; those are checkout's job.
 *   The summary shows the reference's Delivery/HST rows as "Calculated at
 *   checkout" (the drawer's convention), except Delivery's "Free" from
 *   `FREE_SHIPPING_THRESHOLD`, and no Total row — a total that merely repeats the
 *   subtotal would be noise.
 * - **Empty cart** keeps its confirm modal (bulk destructive). Per-line remove
 *   lost its modal ON PURPOSE: the drawer/reducer toast already provides one-tap
 *   Undo, and confirm-then-undo is two safety nets for one action.
 *
 * Line identity matches the drawer and the listing card: `name` = `itemid`,
 * `sku` = `mpn`. Quantity/selection/subscription read LIVE from `state.cart.items`
 * (the details snapshot goes stale between refetches — same lesson as the drawer).
 * "Schedule this order" navigates to `/re-order`, the same adaptation as the
 * drawer's band (the reference's in-cart scheduling flow is a backlog item).
 */
export default function CartPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { cartDetails, cartDetailsLoading } = useCartDetails();
  const cartItems = useSelector((state) => state.cart.items);

  const [showFilter, setShowFilter] = useState("all");
  const [clearCartModalOpen, setClearCartModalOpen] = useState(false);

  // Cart-owned fields live from the slice; product fields from the fetched details.
  const liveById = useMemo(
    () => new Map(cartItems.map((item) => [item.id, item])),
    [cartItems]
  );
  const lines = useMemo(
    () =>
      cartDetails
        .filter((item) => liveById.has(item.id))
        .map((item) => ({ ...item, ...liveById.get(item.id) })),
    [cartDetails, liveById]
  );

  const filteredLines = useMemo(() => {
    if (showFilter === "sub")
      return lines.filter((i) => !!i.custrecord_ci_sub_enabled);
    if (showFilter === "one")
      return lines.filter((i) => !i.custrecord_ci_sub_enabled);
    return lines;
  }, [lines, showFilter]);

  const selectedLines = useMemo(() => lines.filter((i) => i.selected), [lines]);
  const subtotal = useSelector((state) =>
    selectCartSubtotalWithDiscounts(state, selectedLines)
  );
  const totalQuantity = useMemo(
    () => selectedLines.reduce((sum, item) => sum + item.quantity, 0),
    [selectedLines]
  );

  // The reference's appliedPromos breakdown: one row per selected line whose
  // pricing actually discounts, labelled with the NetSuite promotion when the
  // payload names one.
  const promoRows = useMemo(
    () =>
      selectedLines
        .map((item) => {
          const { savePrice } = getCurrentPrice(item, item.quantity);
          if (savePrice <= 0) return null;
          return {
            id: item.id,
            label: item.promotion_name || item.itemid,
            save: savePrice * item.quantity,
          };
        })
        .filter(Boolean),
    [selectedLines]
  );
  const savings = promoRows.reduce((sum, row) => sum + row.save, 0);

  const freeShip = subtotal >= FREE_SHIPPING_THRESHOLD;

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity === 0) dispatch(removeFromCart(itemId));
    else dispatch(updateQuantity({ id: itemId, quantity: newQuantity }));
  };

  const handleConfirmClearCart = () => {
    delayCall(() => dispatch(clearCart()));
    setClearCartModalOpen(false);
    ToastNotification.success("Cart emptied", { sub: "All items removed" });
  };

  // Unchanged semantics: only SELECTED ids go to checkout, unavailable items block.
  const handleProceedToCheckout = () => {
    const unavailableItems = lines.filter((item) => !item.isAvailable);
    if (unavailableItems.length > 0) {
      ToastNotification.error("Some items are no longer available", {
        sub: `Remove ${unavailableItems.length} unavailable item${
          unavailableItems.length !== 1 ? "s" : ""
        } before checking out`,
      });
      return;
    }
    const selectedIds = selectedLines.map((i) => i.id);
    navigate("/checkout", { state: { selectedIds } });
  };

  if (cartDetailsLoading && cartDetails.length === 0) return <Loading />;

  const hasItems = cartItems.length > 0;

  return (
    <div className="mx-auto max-w-[var(--container-lg)] px-[var(--gutter)] pb-[var(--space-20)] pt-[var(--space-7)]">
      <Breadcrumb path={["Home", "Cart"]} />

      {hasItems ? (
        <>
          <div className="mb-[var(--space-6)] mt-[var(--space-4)] flex flex-wrap items-end justify-between gap-[var(--space-4)]">
            <div>
              <div className="mb-[5px] text-2xs font-bold uppercase tracking-[var(--tracking-wider)] text-ink-3">
                {lines.length} product{lines.length !== 1 ? "s" : ""} ·{" "}
                {totalQuantity} selected item{totalQuantity !== 1 ? "s" : ""}
              </div>
              <h1 className="font-sans text-3xl font-semibold leading-[1.1] text-ink">
                Your cart
              </h1>
            </div>
            <div className="flex items-center gap-[var(--space-3)]">
              {/* DS Select takes no ...rest, so the accessible name comes from a
                  labelled group (its own doc's recipe). */}
              <div className="select-pill" role="group" aria-label="Show items">
                <Select
                  value={showFilter}
                  onChange={setShowFilter}
                  options={[
                    { value: "all", label: "All items" },
                    { value: "sub", label: "Subscriptions" },
                    { value: "one", label: "One-time purchases" },
                  ]}
                />
              </div>
              <button
                onClick={() => setClearCartModalOpen(true)}
                className="inline-flex h-[38px] shrink-0 cursor-pointer items-center gap-[6px] rounded-pill border-0 bg-transparent px-[10px] font-sans text-xs font-semibold text-ink-3 [transition:color_var(--duration-fast),background_var(--duration-fast)] hover:bg-error-50 hover:text-error-600"
              >
                <Icon name="remove_shopping_cart" size={18} />
                Empty cart
              </button>
            </div>
          </div>

          <div className="grid items-start gap-[var(--space-7)] lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
            {/* Lines card */}
            <div className="rounded-xl border border-line-subtle bg-card px-[var(--space-6)] py-[var(--space-2)] shadow-sm">
              {filteredLines.length === 0 ? (
                <p className="py-[var(--space-8)] text-center text-sm text-ink-2">
                  No items match that filter.
                </p>
              ) : (
                filteredLines.map((item, index) => {
                  const { basePrice, currentPrice, savePrice } =
                    getCurrentPrice(item, item.quantity);
                  const subscribed = !!item.custrecord_ci_sub_enabled;
                  return (
                    <div
                      key={item.id}
                      className={
                        index !== filteredLines.length - 1
                          ? "border-b border-line-subtle"
                          : ""
                      }
                    >
                      <div className="flex items-start gap-[var(--space-3)]">
                        <Checkbox
                          shape="round"
                          checked={!!item.selected}
                          onChange={() =>
                            dispatch(
                              setItemSelected({
                                id: item.id,
                                selected: !item.selected,
                              })
                            )
                          }
                          aria-label={`Include ${item.itemid} in checkout`}
                          className="mt-[var(--space-5)]"
                        />
                        <div className="min-w-0 flex-1">
                          <CartLineItem
                            style={{ borderBottomWidth: 0 }}
                            name={item.itemid || "Unavailable item"}
                            sku={item.mpn || undefined}
                            media={
                              <Link
                                to={`/product/${item.id}`}
                                aria-label={item.itemid}
                                className="block h-full w-full"
                              >
                                <ProductImage
                                  src={item.file_url}
                                  alt={item.itemid}
                                  className="h-full w-full object-cover"
                                />
                              </Link>
                            }
                            price={currentPrice}
                            quantity={item.quantity}
                            unit={unitLabel(item)}
                            total={currentPrice * item.quantity}
                            original={
                              savePrice > 0
                                ? basePrice * item.quantity
                                : undefined
                            }
                            promo={
                              savePrice > 0
                                ? `You save ${formatCurrency(
                                    savePrice * item.quantity
                                  )}`
                                : undefined
                            }
                            hint={
                              getPromoHint(item, item.quantity) ||
                              (subscribed
                                ? `On auto-delivery · next ${formatLocalDateToronto(
                                    nextFromToday(
                                      item.custrecord_ci_sub_interval || "1"
                                    )
                                  )}`
                                : undefined)
                            }
                            math
                            onQuantityChange={(quantity) =>
                              handleQuantityChange(item.id, quantity)
                            }
                            onRemove={() => dispatch(removeFromCart(item.id))}
                          />
                          {!item.isAvailable && (
                            <p className="mb-[var(--space-3)] mt-[-6px] text-xs font-semibold text-error-600">
                              No longer available — remove this item before
                              checkout.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Order summary */}
            <aside className="sticky top-[200px] flex flex-col gap-[14px] rounded-xl border border-line-subtle bg-card px-[var(--space-6)] py-[var(--space-6)] shadow-sm">
              <div className="text-md font-bold">Order summary</div>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-ink-2">
                  Subtotal · {totalQuantity} item
                  {totalQuantity !== 1 ? "s" : ""}
                </span>
                <FlipAmount
                  text={formatCurrency(subtotal)}
                  className="text-lg font-bold"
                />
              </div>
              {savings > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-2">Promo savings</span>
                    <span className="font-semibold text-success-600">
                      −{formatCurrency(savings)}
                    </span>
                  </div>
                  {promoRows.map((row) => (
                    <div
                      key={row.id}
                      className="flex justify-between gap-[10px] pl-[10px] text-xs"
                    >
                      <span className="min-w-0 truncate text-ink-3">
                        {row.label}
                      </span>
                      <span className="shrink-0 font-semibold text-success-700">
                        −{formatCurrency(row.save)}
                      </span>
                    </div>
                  ))}
                </>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-ink-2">Delivery</span>
                <span>{freeShip ? "Free" : "Calculated at checkout"}</span>
              </div>
              <div className="flex justify-between border-b border-line-subtle pb-[var(--space-3)] text-sm">
                <span className="text-ink-2">HST</span>
                <span>Calculated at checkout</span>
              </div>
              <Button
                fullWidth
                size="lg"
                endIcon="chevron_right"
                disabled={selectedLines.length === 0}
                onClick={handleProceedToCheckout}
              >
                Checkout
              </Button>
              {/* <Button
                fullWidth
                variant="outline"
                startIcon="event_repeat"
                onClick={() => navigate("/re-order")}
              >
                Schedule this order
              </Button> */}
              <div className="flex items-center justify-center gap-[var(--space-2)] text-xs text-ink-3">
                <Icon name="lock" size={16} />
                Secure checkout · pay on account or by card
              </div>
            </aside>
          </div>
        </>
      ) : (
        <div className="mt-[var(--space-6)] rounded-xl border border-line-subtle bg-card px-[var(--space-6)] py-[var(--space-16)] text-center shadow-sm">
          <span className="mb-[18px] inline-flex h-[72px] w-[72px] items-center justify-center rounded-full bg-soft text-blue-600">
            <Icon name="shopping_cart" size={36} />
          </span>
          <h2 className="mb-[var(--space-2)] text-2xl font-semibold">
            Your cart is empty
          </h2>
          <p className="mx-auto mb-[22px] mt-0 max-w-[46ch] text-md text-ink-2">
            Add products as you browse — or pull up a saved list to fill it
            fast.
          </p>
          <div className="flex flex-wrap justify-center gap-[var(--space-3)]">
            <Button
              size="lg"
              startIcon="storefront"
              onClick={() => navigate("/products")}
            >
              Shop the catalogue
            </Button>
            <Button
              size="lg"
              variant="outline"
              startIcon="history"
              onClick={() => navigate("/re-order")}
            >
              Order again
            </Button>
          </div>
        </div>
      )}

      {clearCartModalOpen && (
        <Modal
          title="Empty cart"
          onClose={() => setClearCartModalOpen(false)}
          onSubmit={handleConfirmClearCart}
          onCloseText="Cancel"
          onSubmitText="Empty cart"
        >
          <p className="mb-4 text-gray-700">
            Are you sure you want to remove <strong>all {lines.length}</strong>{" "}
            item
            {lines.length !== 1 ? "s" : ""} from your cart?
          </p>
          <p className="text-sm text-gray-500">This action cannot be undone.</p>
        </Modal>
      )}
    </div>
  );
}
